import { useTranslation } from "react-i18next";

import { StyledFileInput } from "@/components/shared/StyledFileInput";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { QuoteIntakeAnticipatedDocument } from "@/services/quoteIntakeService";
import type { QuoteRequestAttachmentUpload } from "@/services/quoteRequestsService";
import {
  AnticipatedItemFields,
  ANTICIPATED_ACCEPT,
  anticipatedSectionCopy,
} from "./anticipated-item-fields";
import {
  MAX_ANTICIPATED_SLOTS,
  QUOTE_REQUEST_MAX_ATTACHMENTS,
  fileSlotLabels,
  looseAttachmentsOf,
  setLooseSlot as setLooseSlotIn,
  setSlotFiles,
} from "./quote-attachment-slots";

export { QUOTE_REQUEST_MAX_ATTACHMENTS };

/** Props for the free-form title/description/attachments fields of the quote_request step. */
export interface QuoteRequestFieldsProps {
  title: string;
  onTitleChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  attachments: QuoteRequestAttachmentUpload[];
  onAttachmentsChange: (files: QuoteRequestAttachmentUpload[]) => void;
  /**
   * Documents the conversation suggested for this need (spec c04ed435).
   * Empty on the static fallback form, which has no conversation behind it —
   * that path renders the generic slots it always did.
   */
  anticipatedDocuments?: QuoteIntakeAnticipatedDocument[];
  /** Typed answers to the text-kind items, keyed by label (spec 13810fd2). */
  intakeAnswers?: Record<string, string>;
  onIntakeAnswersChange?: (answers: Record<string, string>) => void;
}

/** Controlled fields for the quote_request wizard step — title, description, up to QUOTE_REQUEST_MAX_ATTACHMENTS attachments. */
export function QuoteRequestFields({
  title,
  onTitleChange,
  description,
  onDescriptionChange,
  attachments,
  onAttachmentsChange,
  anticipatedDocuments = [],
  intakeAnswers = {},
  onIntakeAnswersChange,
}: QuoteRequestFieldsProps): JSX.Element {
  const { t } = useTranslation();

  /**
   * Named slots are addressed by label, not by index: the generic slots below
   * add and remove entries, so an index-based lookup would re-point a named
   * slot at somebody else's file the moment a loose attachment is dropped.
   *
   * Capped by MAX_ANTICIPATED_SLOTS, not by the attachment cap. Those were one
   * number until spec b08814a3 and are not the same question — see the
   * constants' own docblocks.
   */
  const namedSlots = anticipatedDocuments.slice(0, MAX_ANTICIPATED_SLOTS);
  // File slots only: a text item holds no attachment, so its label must not be
  // allowed to claim a loose file that happens to share it.
  const namedLabels = fileSlotLabels(namedSlots);
  const looseAttachments = looseAttachmentsOf(attachments, namedLabels);

  const changeSlotFiles = (doc: QuoteIntakeAnticipatedDocument, files: File[]): void => {
    onAttachmentsChange(setSlotFiles(attachments, doc, files));
  };

  const setLooseSlot = (index: number, file: File | null): void => {
    onAttachmentsChange(setLooseSlotIn(attachments, namedLabels, index, file));
  };

  const remaining = Math.max(0, QUOTE_REQUEST_MAX_ATTACHMENTS - attachments.length);
  // One filled slot per loose file, plus one empty slot to add the next —
  // suppressed once the overall cap is reached.
  const looseSlotCount = looseAttachments.length + (remaining > 0 ? 1 : 0);
  const atMax = attachments.length >= QUOTE_REQUEST_MAX_ATTACHMENTS;

  const anticipatedCopy = anticipatedSectionCopy(namedSlots);

  const setAnswer = (label: string, value: string): void => {
    onIntakeAnswersChange?.({ ...intakeAnswers, [label]: value });
  };

  return (
    <Card className="border-border/80 shadow-sm dark:border-border/80 dark:bg-secondary/35 dark:shadow-md dark:shadow-black/20">
      <CardContent className="space-y-6 pt-6">
        <div className="space-y-2">
          <Label htmlFor="quote-request-title" className="text-sm font-medium text-foreground">
            {t("requests.user.quoteRequestTitleLabel")}
          </Label>
          <Input
            id="quote-request-title"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder={t("requests.user.quoteRequestTitlePlaceholder")}
            className="bg-card dark:bg-background"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="quote-request-description" className="text-sm font-medium text-foreground">
            {t("requests.user.quoteRequestDescriptionLabel")}
          </Label>
          <Textarea
            id="quote-request-description"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder={t("requests.user.quoteRequestDescriptionPlaceholder")}
            rows={6}
            className="bg-card dark:bg-background"
          />
        </div>

        {/* THE SECOND REGION (spec b08814a3, D5).
            Above this line the consumer is writing the request professionals
            will read; below it they are optionally handing over data that
            sharpens the quote. Those are two different jobs and used to be one
            undifferentiated stack of fields. The rule divides them; the
            heading names the second one. */}
        <div className="space-y-4 border-t border-border pt-5">
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-foreground">
              {/* The cap is INTERPOLATED, never written into the string. It
                  read "hasta 5" while the counter underneath said "0 de 20",
                  because the copy was a literal and the counter already used
                  the constant. Two numbers for one limit, disagreeing, in the
                  same block. */}
              {t(namedSlots.length > 0 ? anticipatedCopy.labelKey : "requests.user.quoteRequestAttachmentLabel", {
                max: QUOTE_REQUEST_MAX_ATTACHMENTS,
              })}
            </h3>
            {/* Says "optional" in the heading, not buried per-slot: the
                consumer has no quote in hand yet, so this must never read as
                a requirement standing between them and submitting. */}
            {namedSlots.length > 0 && (
              <p className="text-sm leading-relaxed text-muted-foreground">{t(anticipatedCopy.hintKey)}</p>
            )}
          </div>

          {namedSlots.length > 0 && (
            <AnticipatedItemFields
              idPrefix="quote-request-doc"
              items={namedSlots}
              attachments={attachments}
              onSlotFilesChange={changeSlotFiles}
              answers={intakeAnswers}
              onAnswerChange={setAnswer}
              remainingAttachments={remaining}
            />
          )}

          <div className="space-y-2">
            {namedSlots.length > 0 && (
              <Label className="text-sm font-normal text-muted-foreground">
                {t("requests.user.quoteRequestOtherAttachmentLabel")}
              </Label>
            )}
            <div className="space-y-2">
              {Array.from({ length: looseSlotCount }, (_, index) => (
                <StyledFileInput
                  key={index}
                  id={`quote-request-attachment-${index}`}
                  accept={ANTICIPATED_ACCEPT}
                  value={looseAttachments[index]?.file ?? null}
                  onChange={(file) => setLooseSlot(index, file)}
                />
              ))}
            </div>
            {/* The running count, always — not just the ceiling message once
                it is hit. A consumer part-way through twelve invoices needs to
                know where the limit is BEFORE they run into it. */}
            <p className="text-xs text-muted-foreground">
              {atMax
                ? t("requests.user.quoteRequestAttachmentMaxReached", {
                    max: QUOTE_REQUEST_MAX_ATTACHMENTS,
                  })
                : // `n`, not `count`: i18next reserves `count` as the plural
                  // selector, and with only a base key there is no _one/_other
                  // variant for it to resolve to.
                  t("requests.user.quoteRequestAttachmentCount", {
                    n: attachments.length,
                    max: QUOTE_REQUEST_MAX_ATTACHMENTS,
                  })}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
